import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import useCart from '../hooks/useCart';
import { subscribeOpen, cartCount } from '../lib/cart';
import { track, FUNNEL } from '../lib/track';
import CartContents from './CartContents';

// Slide-out cart. Mount once inside BrowserRouter, next to ScrollToTop.
//
// This IS the cart. Adding something opens it over the page you were on; it
// shows delivery date and charge, the old-AC exchange, the total, and one
// button to checkout. The /cart page renders the same CartContents for anyone
// who lands there directly.

export default function CartDrawer() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const lines = useCart();
  const count = cartCount(lines);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => subscribeOpen(() => {
    setOpen(true);
    track(FUNNEL.CART_VIEW, { source: 'drawer' });
  }), []);

  // Scroll lock while the panel is up. Always cleared rather than restored:
  // restoring a captured value once left the page permanently unscrollable
  // when the drawer re-opened before cleanup ran.
  useEffect(() => {
    if (!open) {
      document.body.style.overflow = '';
      return undefined;
    }
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, close]);

  useEffect(() => () => { document.body.style.overflow = ''; }, []);

  // Close on navigation, including the Checkout button inside the drawer.
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <>
      <div
        onClick={close}
        aria-hidden="true"
        className={`fixed inset-0 z-[60] bg-black/40 transition-opacity duration-200 ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />

      <aside
        role="dialog"
        aria-modal="true"
        inert={!open}
        aria-label="Shopping cart"
        className={`fixed right-0 top-0 z-[61] flex h-full w-full max-w-[420px] flex-col bg-white shadow-2xl transition-transform duration-300 ease-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 px-4">
          <h2 className="text-base font-bold text-gray-900">
            Your cart{count > 0 && <span className="ml-1.5 font-normal text-gray-500">({count})</span>}
          </h2>
          <button type="button" onClick={close} aria-label="Close cart"
            className="flex h-11 w-11 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-gray-800">
            <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        {open && <CartContents variant="drawer" onClose={close} />}
      </aside>
    </>
  );
}
